import { useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { Add, Delete } from '@mui/icons-material'

import {
    useCreateProjectKeyMutation,
    useListProjectKeysQuery,
    useRevokeProjectKeyMutation,
} from '@/apis/opsApi'
import {
    PROJECT_KEY_SCOPES,
    isLegacyProjectKey,
    projectKeyType,
    type ProjectKey,
    type ProjectKeyCreated,
    type ProjectKeyScope,
} from '@/apis/opsTypes'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { CopyButton } from '@/components/shared/CopyButton'
import { ReportStatus, fmtDateTime } from '@/modules/reports/common'

const SCOPE_HELP: Record<ProjectKeyScope, string> = {
    ingest: 'Send events from browsers and apps (publishable, safe to embed)',
    server: 'Send server-side events (POST /v2/events, bearer or HMAC)',
    read: 'Read reports and raw events',
    write: 'Manage goals, funnels, destinations and project settings',
}

function KeyRow({
    k,
    legacy,
    onRevoke,
}: {
    k: ProjectKey
    legacy: boolean
    onRevoke: (k: ProjectKey) => void
}) {
    return (
        <TableRow sx={legacy ? { opacity: 0.75 } : undefined}>
            <TableCell>
                <Typography variant="body2" fontWeight={600}>
                    {k.name}
                </Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    {k.key_prefix}…
                </Typography>
                {!legacy && projectKeyType(k.scopes) === 'sk' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace' }}
                        >
                            {`Key id ${k.id}`}
                        </Typography>
                        <CopyButton text={k.id} />
                    </Box>
                )}
            </TableCell>
            <TableCell>
                <Chip
                    size="small"
                    variant="outlined"
                    label={legacy ? 'v1' : projectKeyType(k.scopes)}
                    color={legacy ? 'warning' : 'default'}
                />
            </TableCell>
            <TableCell>
                {k.scopes.map(s => (
                    <Chip key={s} size="small" label={s} sx={{ mr: 0.5, mb: 0.5 }} />
                ))}
            </TableCell>
            <TableCell>{k.sub_tenant ?? 'all'}</TableCell>
            <TableCell>{k.last_used_at ? fmtDateTime(k.last_used_at) : 'never'}</TableCell>
            <TableCell>{k.expires_at ? fmtDateTime(k.expires_at) : '—'}</TableCell>
            <TableCell align="right">
                <Tooltip title="Revoke">
                    <IconButton
                        size="small"
                        aria-label={`Revoke ${k.name}`}
                        onClick={() => onRevoke(k)}
                    >
                        <Delete fontSize="small" />
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    )
}

function KeyTable({
    keys,
    legacy,
    onRevoke,
}: {
    keys: ProjectKey[]
    legacy: boolean
    onRevoke: (k: ProjectKey) => void
}) {
    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Name / key</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Scopes</TableCell>
                    <TableCell>Sub-tenant</TableCell>
                    <TableCell>Last used</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell />
                </TableRow>
            </TableHead>
            <TableBody>
                {keys.map(k => (
                    <KeyRow key={k.id} k={k} legacy={legacy} onRevoke={onRevoke} />
                ))}
            </TableBody>
        </Table>
    )
}

/**
 * v2 project keys: publishable `pk` (ingest only) and secret `sk` keys, optionally bound to one
 * sub-tenant. Keys made on the old v1 screen are listed separately, read-only except revoke.
 */
export function ProjectKeysCard({ projectId }: { projectId: string }) {
    const {
        data = [],
        isLoading,
        error,
    } = useListProjectKeysQuery(projectId, {
        skip: !projectId,
    })
    const [createKey, createState] = useCreateProjectKeyMutation()
    const [revokeKey] = useRevokeProjectKeyMutation()
    const [open, setOpen] = useState(false)
    const [name, setName] = useState('')
    const [scopes, setScopes] = useState<ProjectKeyScope[]>(['ingest'])
    const [subTenant, setSubTenant] = useState('')
    const [expires, setExpires] = useState('')
    const [created, setCreated] = useState<ProjectKeyCreated | null>(null)
    const [toRevoke, setToRevoke] = useState<ProjectKey | null>(null)

    const active = data.filter(k => !k.revoked_at)
    const v2Keys = active.filter(k => !isLegacyProjectKey(k))
    const v1Keys = active.filter(isLegacyProjectKey)

    const create = async () => {
        try {
            const r = await createKey({
                projectId,
                name: name.trim() || `${projectKeyType(scopes)} key`,
                scopes,
                sub_tenant: subTenant.trim() || null,
                expires_at: expires ? new Date(`${expires}T23:59:59Z`).toISOString() : null,
            }).unwrap()
            setCreated(r)
            setOpen(false)
            setName('')
            setScopes(['ingest'])
            setSubTenant('')
            setExpires('')
        } catch {
            // shown from createState.error
        }
    }

    return (
        <Card>
            <CardContent>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1,
                    }}
                >
                    <Typography variant="h6">Project keys</Typography>
                    <Button
                        size="small"
                        variant="contained"
                        startIcon={<Add />}
                        disabled={!projectId}
                        onClick={() => setOpen(true)}
                    >
                        New key
                    </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {'A publishable '}
                    <b>pk</b>
                    {' key (ingest only) goes in your site or app. Any other scope makes a secret '}
                    <b>sk</b>
                    {
                        " key for your backend. Bind a key to a sub-tenant to limit it to one of your customers' data."
                    }
                </Typography>
                {created && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCreated(null)}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                            {created.type === 'pk' ? 'Publishable' : 'Secret'} key created. Copy it
                            now; it is not shown again.
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                fontFamily: 'monospace',
                                wordBreak: 'break-all',
                            }}
                        >
                            {created.key}
                            <CopyButton text={created.key} />
                        </Box>
                        {created.type === 'sk' && created.hmac && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {`To sign requests with HMAC instead of sending the key, use key id ${created.id} in the YOM-Key-Id header.`}
                            </Typography>
                        )}
                        {created.type === 'sk' && !created.hmac && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                HMAC request signing is not available for this key; use it as a
                                bearer token.
                            </Typography>
                        )}
                    </Alert>
                )}
                <ReportStatus
                    isLoading={isLoading}
                    error={error}
                    isEmpty={active.length === 0}
                    emptyTitle="No keys for this project"
                    emptyDescription="Create a publishable key to start sending events."
                >
                    {v2Keys.length > 0 && (
                        <KeyTable keys={v2Keys} legacy={false} onRevoke={setToRevoke} />
                    )}
                    {v1Keys.length > 0 && (
                        <Box sx={{ mt: 3 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                v1 keys (deprecated)
                            </Typography>
                            <Alert severity="warning" sx={{ mb: 1 }}>
                                These keys were created on the old API Keys screen with v1 scopes
                                (such as <code>admin</code>). They keep working, but can&apos;t be
                                created or changed any more. Create a v2 key with only the scopes
                                you need, switch your integration to it, then revoke the v1 key.
                            </Alert>
                            <KeyTable keys={v1Keys} legacy onRevoke={setToRevoke} />
                        </Box>
                    )}
                </ReportStatus>
            </CardContent>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New project key</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {createState.error && (
                        <Alert severity="error">{v2ErrorMessage(createState.error)}</Alert>
                    )}
                    <TextField
                        size="small"
                        label="Name"
                        placeholder="Website"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        sx={{ mt: 1 }}
                    />
                    <FormGroup>
                        {PROJECT_KEY_SCOPES.map(s => (
                            <FormControlLabel
                                key={s}
                                control={
                                    <Checkbox
                                        checked={scopes.includes(s)}
                                        onChange={e =>
                                            setScopes(
                                                e.target.checked
                                                    ? [...scopes, s]
                                                    : scopes.filter(x => x !== s),
                                            )
                                        }
                                    />
                                }
                                label={`${s}: ${SCOPE_HELP[s]}`}
                            />
                        ))}
                    </FormGroup>
                    <Alert severity="info">
                        This will be a{' '}
                        {projectKeyType(scopes) === 'pk'
                            ? 'publishable pk key: safe to embed in a page.'
                            : 'secret sk key: keep it on your server.'}
                    </Alert>
                    <TextField
                        size="small"
                        label="Only this sub-tenant (optional)"
                        helperText="The key can only send and read this sub-tenant's data."
                        value={subTenant}
                        onChange={e => setSubTenant(e.target.value)}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="Expires (optional)"
                        value={expires}
                        onChange={e => setExpires(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={scopes.length === 0 || createState.isLoading}
                        onClick={() => {
                            void create()
                        }}
                    >
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
            <ConfirmDialog
                open={!!toRevoke}
                title="Revoke key?"
                message={`Anything using ${toRevoke?.name ?? 'this key'} (${toRevoke?.key_prefix ?? ''}…) stops working immediately. This cannot be undone.`}
                confirmLabel="Revoke"
                confirmColor="error"
                onConfirm={() => {
                    if (toRevoke) {
                        void revokeKey({ projectId, id: toRevoke.id })
                    }
                    setToRevoke(null)
                }}
                onCancel={() => setToRevoke(null)}
            />
        </Card>
    )
}
